import { MAX_FAILED_ELECTIONS, MAX_PLAYERS } from 'shared';
import { Avatar, type AvatarSubject } from '../design/Avatar';
import { Button } from '../design/Button';
import { ChevronBand, Halftone, Label, Paper, Rule } from '../design/Paper';
import { Stamp } from '../design/Stamp';
import { Tracker } from '../design/Tracker';
import { VoteCard } from '../design/VoteCard';
import { Panel } from '../design/Panel';
import { ro } from '../i18n/ro';
import { playerColor, tiltFor } from '../design/tokens';

/**
 * Not part of the app. A single page showing every component in every state, so
 * the design can be judged and regressions spotted without running a game.
 * Reachable at /style.
 */

const PEOPLE: AvatarSubject[] = [
  { id: '1', name: 'Ana Popescu', initials: 'AP', colorIndex: 0, hasAvatar: false, avatarVersion: 0 },
  { id: '2', name: 'Bogdan Marin', initials: 'BM', colorIndex: 1, hasAvatar: false, avatarVersion: 0 },
  { id: '3', name: 'Cristina', initials: 'CR', colorIndex: 2, hasAvatar: false, avatarVersion: 0 },
  { id: '4', name: 'Ștefan Țuca', initials: 'ȘȚ', colorIndex: 3, hasAvatar: false, avatarVersion: 0 },
  { id: '5', name: 'Dan', initials: 'DA', colorIndex: 4, hasAvatar: false, avatarVersion: 0 },
];

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="mb-14">
      <div className="mb-5 flex items-baseline gap-4">
        <Label className="text-xs text-ink-soft">{title}</Label>
        <div className="h-px flex-1 bg-ink/25" />
      </div>
      {children}
    </section>
  );
}

export function StylePlate() {
  return (
    <Paper className="min-h-screen" vignette={false}>
      <div className="mx-auto max-w-6xl px-6 py-10">
        <header className="mb-12">
          <ChevronBand className="mb-4 text-ink" />
          <h1 className="font-display text-4xl uppercase tracking-tight">
            {ro.appName} — placă de stil
          </h1>
          <p className="mt-2 text-sm text-ink-soft">
            Weimar / afiș tipărit · hârtie crem · Ja teal, Nein roșu
          </p>
          <Rule className="mt-4" />
        </header>

        <Section title="Paletă">
          <div className="flex flex-wrap gap-3">
            {[
              ['paper', 'var(--color-paper)', 'ink'],
              ['paper-raised', 'var(--color-paper-raised)', 'ink'],
              ['paper-sunk', 'var(--color-paper-sunk)', 'ink'],
              ['ink', 'var(--color-ink)', 'paper'],
              ['red · Nein', 'var(--color-red)', 'paper'],
              ['teal · Ja', 'var(--color-teal)', 'paper'],
              ['gold', 'var(--color-gold)', 'ink'],
            ].map(([name, value, fg]) => (
              <div
                key={name}
                className="flex h-20 w-32 items-end border-[3px] border-ink p-2"
                style={{ background: value }}
              >
                <span
                  className="deco-caps text-[0.6rem]"
                  style={{ color: fg === 'paper' ? 'var(--color-paper)' : 'var(--color-ink)' }}
                >
                  {name}
                </span>
              </div>
            ))}
          </div>
        </Section>

        <Section title={`Culori jucători (${MAX_PLAYERS}, unice)`}>
          <div className="flex flex-wrap gap-1.5">
            {Array.from({ length: MAX_PLAYERS }, (_, i) => (
              <div
                key={i}
                className="flex h-9 w-9 items-center justify-center border-2 border-ink font-display text-[0.6rem] text-paper-raised"
                style={{ background: playerColor(i) }}
              >
                {i}
              </div>
            ))}
          </div>
        </Section>

        <Section title="Tipografie">
          <div className="space-y-3">
            <div className="font-display text-screen-hero uppercase leading-[0.9]">Ja / Nein</div>
            <div className="font-display text-4xl uppercase">Guvern în funcție</div>
            <div className="deco-caps text-sm text-ink-soft">Președinte · Cancelar</div>
            <p className="max-w-xl text-base">
              Diacritice: Șș Țț Ăă Îî Ââ — Ștefan Țăndărică a înființat un guvern.
            </p>
            <p className="max-w-xl text-sm text-ink-faint">
              Text secundar, doar pe telefon. Nimic de pe proiector nu are voie să fie ton mediu.
            </p>
          </div>
        </Section>

        <Section title="Avatare">
          <div className="flex flex-wrap items-end gap-5">
            {(['sm', 'md', 'lg'] as const).map((size) => (
              <div key={size} className="flex items-end gap-2">
                {PEOPLE.slice(0, 3).map((p) => (
                  <Avatar key={p.id} subject={p} roomToken={null} size={size} />
                ))}
              </div>
            ))}
            <div className="flex items-end gap-2">
              <Avatar subject={PEOPLE[0]!} roomToken={null} size="lg" dimmed />
              <Label className="mb-2 text-[0.6rem] text-ink-faint">executat</Label>
            </div>
          </div>
        </Section>

        <Section title="Cartonașe de vot">
          <div className="flex flex-wrap items-end gap-8">
            <div className="flex items-end gap-2">
              <VoteCard face="PENDING" />
              <VoteCard face="DOWN" />
              <VoteCard face="JA" />
              <VoteCard face="NEIN" />
              <Label className="mb-3 text-[0.6rem] text-ink-faint">md</Label>
            </div>
            <div className="flex items-end gap-1.5">
              <VoteCard face="DOWN" size="sm" />
              <VoteCard face="JA" size="sm" />
              <VoteCard face="NEIN" size="sm" />
              <Label className="mb-2 text-[0.6rem] text-ink-faint">sm</Label>
            </div>
          </div>
        </Section>

        <Section title="Ștampile">
          <div className="flex flex-wrap items-center gap-4">
            <Stamp tone="teal" className={tiltFor('a')}>{ro.governmentApproved}</Stamp>
            <Stamp tone="red" className={tiltFor('b')}>{ro.governmentRejected}</Stamp>
            <Stamp tone="ink" size="sm" className={tiltFor('c')}>{ro.termLimited}</Stamp>
            <Stamp tone="red" size="sm" className={tiltFor('d')}>{ro.executed}</Stamp>
          </div>
        </Section>

        <Section title="Butoane">
          <div className="mb-5 flex flex-wrap items-center gap-3">
            <Button tone="ink">{ro.startVote}</Button>
            <Button tone="paper">{ro.setGovernment}</Button>
            <Button tone="red">{ro.annulVote}</Button>
            <Button tone="ghost" size="sm">{ro.rename}</Button>
            <Button tone="paper" size="sm" disabled>{ro.kick}</Button>
          </div>
          <div className="flex max-w-md gap-4">
            <Button tone="teal" size="vote" block>{ro.ja}</Button>
            <Button tone="red" size="vote" block>{ro.nein}</Button>
          </div>
        </Section>

        <Section title="Contor alegeri eșuate">
          <div className="space-y-3">
            {Array.from({ length: MAX_FAILED_ELECTIONS + 1 }, (_, n) => (
              <Tracker key={n} failedElections={n} />
            ))}
          </div>
        </Section>

        <Section title="Panouri">
          <div className="grid gap-5 sm:grid-cols-3">
            <Panel innerClassName="p-5">
              <Label className="text-[0.65rem] text-ink-faint">{ro.president}</Label>
              <div className="mt-2 font-display text-2xl uppercase">Ana Popescu</div>
            </Panel>
            <Panel tone="ink" innerClassName="relative overflow-hidden p-5">
              <Halftone className="text-paper" />
              <div className="relative">
                <Label className="text-[0.65rem] opacity-70">{ro.chancellor}</Label>
                <div className="mt-2 font-display text-2xl uppercase">Bogdan Marin</div>
              </div>
            </Panel>
            <Panel tone="red" innerClassName="relative overflow-hidden p-5">
              <Halftone className="text-paper" />
              <div className="relative font-display text-xl uppercase leading-tight">
                {ro.chaosTitle}
              </div>
            </Panel>
          </div>
        </Section>

        <Section title="Guvern (compus)">
          <Panel tone="sunk" innerClassName="p-6">
            <div className="flex items-center justify-center gap-8">
              {[
                [ro.president, PEOPLE[0]!],
                [ro.chancellor, PEOPLE[1]!],
              ].map(([role, person]) => (
                <div key={role as string} className="text-center">
                  <Panel rule innerClassName="p-3">
                    <Avatar subject={person as AvatarSubject} roomToken={null} size="lg" />
                  </Panel>
                  <div className="mt-3 font-display text-lg uppercase">
                    {(person as AvatarSubject).name}
                  </div>
                  <Label className="text-[0.6rem] text-ink-faint">{role as string}</Label>
                </div>
              ))}
            </div>
          </Panel>
        </Section>

        <Section title="Rând de voturi (proiector)">
          <Panel tone="sunk" innerClassName="p-6">
            <div className="flex flex-wrap justify-center gap-6">
              {PEOPLE.map((p, i) => (
                <div key={p.id} className="flex flex-col items-center gap-2">
                  <VoteCard face={(['JA', 'NEIN', 'JA', 'DOWN', 'PENDING'] as const)[i]!} />
                  <Avatar subject={p} roomToken={null} size="sm" dimmed={i === 4} />
                  <span className="max-w-20 truncate text-center text-xs">{p.name}</span>
                </div>
              ))}
            </div>
          </Panel>
        </Section>
      </div>
    </Paper>
  );
}
